import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import GoogleMap, { MapLocation } from "../GoogleMap/GoogleMap.tsx";
import {
  Site,
  Organization,
  IQWatchDeviceWithData,
} from "../../types";
import { Activity, MapPin, AlertTriangle, Loader2 } from "lucide-react";
import SiteDetailSlidePanel from "./SiteDetailSlidePanel.tsx";

interface DashboardMapViewProps {
  organizations: Organization[];
  selectedSite?: Site | null;
  selectedOrganization?: Organization | null;
  iqwatchDevices: IQWatchDeviceWithData[];
  apiKey?: string;
  loading?: boolean;
  error?: string;
  onSiteSelect?: (site: Site) => void;
  onDeviceClick?: (
    device: IQWatchDeviceWithData,
    tab?: "battery" | "router" | "cameras"
  ) => void;
}

interface SiteDeviceSummary {
  site: Site;
  iqwatchCount: number;
  onlineCount: number;
  offlineCount: number;
  lowBatteryCount: number;
  averageBattery: number;
}

interface GeocodedCoordinates {
  lat: number;
  lng: number;
  formattedAddress: string;
}

const DashboardMapView: React.FC<DashboardMapViewProps> = ({
  organizations,
  selectedSite,
  selectedOrganization,
  iqwatchDevices,
  apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  loading = false,
  error,
  onSiteSelect,
  onDeviceClick,
}) => {
  const [geocodedSites, setGeocodedSites] = useState<
    Record<string, GeocodedCoordinates>
  >({});
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string>("");
  const [selectedSiteForPanel, setSelectedSiteForPanel] = useState<Site | null>(null);
  const deviceClickCallbackRef = useRef<
    | ((
        device: IQWatchDeviceWithData,
        tab?: "battery" | "router" | "cameras"
      ) => void)
    | undefined
  >(onDeviceClick);
  const devicesRef = useRef<IQWatchDeviceWithData[]>(iqwatchDevices);

  // Update refs when props change
  useEffect(() => {
    deviceClickCallbackRef.current = onDeviceClick;
    devicesRef.current = iqwatchDevices;
  }, [onDeviceClick, iqwatchDevices]);

  // Find organization ID for a given site
  const getOrganizationIdForSite = useCallback(
    (site: Site): string | null => {
      for (const org of organizations) {
        const findSiteInOrg = (sites: Site[], targetId: string): boolean => {
          return sites.some((s) => {
            if (s.id === targetId) return true;
            if (s.children) return findSiteInOrg(s.children, targetId);
            return false;
          });
        };
        if (findSiteInOrg(org.sites, site.id)) {
          return org.id;
        }
      }
      return null;
    },
    [organizations]
  );

  // Geocode address using Google Maps Geocoding API
  const geocodeAddress = useCallback(
    async (address: string): Promise<GeocodedCoordinates | null> => {
      if (!apiKey) {
        console.warn("Google Maps API key is required for geocoding");
        return null;
      }

      try {
        const encodedAddress = encodeURIComponent(address);
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&language=en&key=${apiKey}`
        );

        if (!response.ok) {
          throw new Error("Failed to geocode address");
        }

        const data = await response.json();

        if (data.status === "OK" && data.results.length > 0) {
          const result = data.results[0];
          return {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng,
            formattedAddress: result.formatted_address,
          };
        } else {
          console.warn(
            `Geocoding failed for address: ${address}`,
            data.error_message
          );
          return null;
        }
      } catch (err) {
        console.error(`Error geocoding address ${address}:`, err);
        return null;
      }
    },
    [apiKey]
  );

  // Get all sites with their device summaries including alerts
  const sitesSummary = useMemo((): SiteDeviceSummary[] => {
    const allSites: Site[] = [];
    const addedSiteIds = new Set<string>();

    // Recursively collect all sites
    const collectSites = (sites: Site[]) => {
      sites.forEach((site) => {
        if (!addedSiteIds.has(site.id)) {
          allSites.push(site);
          addedSiteIds.add(site.id);
        }
        if (site.children && site.children.length > 0) {
          collectSites(site.children);
        }
      });
    };

    // Determine which organizations to include
    let orgsToProcess: Organization[] = [];

    if (selectedSite) {
      const collectSelectedSiteAndChildren = (site: Site) => {
        allSites.push(site);
        if (site.children && site.children.length > 0) {
          collectSites(site.children);
        }
      };
      collectSelectedSiteAndChildren(selectedSite);
    } else if (selectedOrganization) {
      orgsToProcess = [selectedOrganization];
    } else {
      orgsToProcess = organizations;
    }

    if (!selectedSite) {
      orgsToProcess.forEach((org) => {
        collectSites(org.sites);
      });
    }

    // Calculate device summaries for each site
    return allSites
      .map((site) => {
        const getSiteIds = (s: Site): string[] => {
          const ids = [s.id];
          if (s.children) {
            s.children.forEach((child) => {
              ids.push(...getSiteIds(child));
            });
          }
          return ids;
        };

        const siteIds = getSiteIds(site);
        const siteIqwatchDevices = iqwatchDevices.filter((device) =>
          siteIds.includes(device.device.site.id)
        );

        const onlineCount = siteIqwatchDevices.filter(
          (device) => device.device.status === "running"
        ).length;
        const offlineCount = siteIqwatchDevices.filter(
          (device) =>
            device.device.status === "stopped" ||
            device.device.status === "error"
        ).length;

        const lowBatteryDevices = siteIqwatchDevices.filter((device) => {
          const soc = device.device.last_soc
            ? parseFloat(device.device.last_soc)
            : NaN;
          return !isNaN(soc) && soc < 50;
        });
        const lowBatteryCount = lowBatteryDevices.length;

        const devicesWithValidSoc = siteIqwatchDevices.filter((device) => {
          const soc = device.device.last_soc
            ? parseFloat(device.device.last_soc)
            : NaN;
          return !isNaN(soc);
        });

        const totalBattery = devicesWithValidSoc.reduce(
          (sum, device) =>
            sum +
            (device.device.last_soc ? parseFloat(device.device.last_soc) : 0),
          0
        );
        const batteryDeviceCount = devicesWithValidSoc.length;

        const summary = {
          site,
          iqwatchCount: siteIqwatchDevices.length,
          onlineCount,
          offlineCount,
          lowBatteryCount,
          averageBattery:
            batteryDeviceCount > 0 ? totalBattery / batteryDeviceCount : 0,
        };

        return summary;
      })
      .filter(
        (summary) => summary.site.detail?.address || summary.iqwatchCount > 0
      );
  }, [
    organizations,
    selectedSite,
    selectedOrganization,
    iqwatchDevices,
  ]);

  // Track addresses to detect changes
  const previousAddressesRef = useRef<Record<string, string>>({});

  // Single unified geocoding effect
  useEffect(() => {
    const currentAddresses: Record<string, string> = {};
    sitesSummary.forEach((summary) => {
      if (summary.site.detail?.address) {
        currentAddresses[summary.site.id] = summary.site.detail.address;
      }
    });

    // Check for address changes and clear cache
    const changedSiteIds: string[] = [];
    Object.keys(currentAddresses).forEach((siteId) => {
      if (currentAddresses[siteId] !== previousAddressesRef.current[siteId]) {
        changedSiteIds.push(siteId);
      }
    });

    // Clear cache for sites with changed addresses
    if (changedSiteIds.length > 0) {
      setGeocodedSites((prev) => {
        const updated = { ...prev };
        changedSiteIds.forEach((siteId) => {
          delete updated[siteId];
        });
        return updated;
      });
    }

    // Find sites that need geocoding
    const sitesToGeocode = sitesSummary.filter(
      (summary) =>
        summary.site.detail?.address && // Has address
        !summary.site.detail?.coordinates && // No coordinates
        !geocodedSites[summary.site.id] // Not already geocoded
    );

    // Geocode new sites
    if (sitesToGeocode.length > 0) {
      const geocodeSites = async () => {
        setGeocodingLoading(true);
        setGeocodingError("");

        const geocodePromises = sitesToGeocode.map(async (summary) => {
          const address = summary.site.detail?.address;
          if (!address) return null;

          const coordinates = await geocodeAddress(address);
          return coordinates ? { siteId: summary.site.id, coordinates } : null;
        });

        try {
          const results = await Promise.all(geocodePromises);
          const newGeocodedSites: Record<string, GeocodedCoordinates> = {};

          results.forEach((result) => {
            if (result) {
              newGeocodedSites[result.siteId] = result.coordinates;
            }
          });

          if (Object.keys(newGeocodedSites).length > 0) {
            setGeocodedSites((prev) => ({ ...prev, ...newGeocodedSites }));
          }
        } catch (err) {
          setGeocodingError("Failed to geocode some addresses");
          console.error("Geocoding error:", err);
        } finally {
          setGeocodingLoading(false);
        }
      };

      geocodeSites();
    }

    // Update the ref with current addresses
    previousAddressesRef.current = currentAddresses;
  }, [sitesSummary, geocodeAddress, geocodedSites]);

  // Convert sites to map locations
  const mapLocations = useMemo((): MapLocation[] => {
    return sitesSummary
      .map((summary) => {
        const { site, offlineCount, lowBatteryCount } = summary;

        let coords = site.detail?.coordinates;
        if (!coords && geocodedSites[site.id]) {
          coords = geocodedSites[site.id];
        }

        if (!coords) return null;

        const hasIssues = offlineCount > 0 || lowBatteryCount > 0;

        return {
          id: site.id,
          lat: coords.lat,
          lng: coords.lng,
          title: site.name,
          description: `<div class="p-2"><h3 class="text-lg font-semibold">${site.name}</h3></div>`,
          address: coords.formattedAddress || site.detail?.address,
          type: site.type === "region" ? "site" : "subsite",
          icon: hasIssues
            ? "custom"
            : site.type === "region"
            ? "site"
            : "subsite",
        };
      })
      .filter(Boolean) as MapLocation[];
  }, [sitesSummary, geocodedSites]);

  // Calculate map center
  const mapCenter = useMemo(() => {
    if (selectedSite?.detail?.coordinates) {
      return {
        lat: selectedSite.detail.coordinates.lat,
        lng: selectedSite.detail.coordinates.lng,
      };
    }

    if (selectedSite?.detail?.address && geocodedSites[selectedSite.id]) {
      const coords = geocodedSites[selectedSite.id];
      return { lat: coords.lat, lng: coords.lng };
    }

    if (mapLocations.length > 0) {
      const avgLat =
        mapLocations.reduce((sum, loc) => sum + loc.lat, 0) /
        mapLocations.length;
      const avgLng =
        mapLocations.reduce((sum, loc) => sum + loc.lng, 0) /
        mapLocations.length;
      return { lat: avgLat, lng: avgLng };
    }

    // Default to US center
    return { lat: 39.8283, lng: -98.5795 };
  }, [selectedSite, mapLocations, geocodedSites]);

  const handleLocationClick = (location: MapLocation) => {
    const site = sitesSummary.find((s) => s.site.id === location.id)?.site;
    if (site) {
      setSelectedSiteForPanel(site);
      if (onSiteSelect) {
        onSiteSelect(site);
      }
    }
  };

  // Update the map zoom logic
  const mapZoom = useMemo(() => {
    if (selectedSite) {
      return 12; // Zoom in for specific site
    } else if (selectedOrganization && mapLocations.length <= 5) {
      return 8; // Medium zoom for small number of sites
    } else {
      return 6; // Wider zoom for multiple sites or all organizations
    }
  }, [selectedSite, selectedOrganization, mapLocations.length]);

  // Update the header to show context
  const getHeaderTitle = () => {
    if (selectedSite) {
      return `${selectedSite.name} - Site View`;
    } else if (selectedOrganization) {
      return `${selectedOrganization.name} - All Sites`;
    } else {
      return "All Customers - Site Overview";
    }
  };

  // Get organization ID for selected site
  const selectedSiteOrgId = selectedSiteForPanel
    ? getOrganizationIdForSite(selectedSiteForPanel)
    : null;

  if (loading || geocodingLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {geocodingLoading
              ? "Loading map and geocoding addresses..."
              : "Loading map data..."}
          </p>
        </div>
      </div>
    );
  }

  if (error || geocodingError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Map Error</h3>
          <p className="text-gray-600">{error || geocodingError}</p>
        </div>
      </div>
    );
  }

  if (mapLocations.length === 0) {
    const sitesWithAddresses = sitesSummary.filter(
      (s) => s.site.detail?.address
    );
    const sitesWithoutCoordinates = sitesWithAddresses.filter(
      (s) => !s.site.detail?.coordinates && !geocodedSites[s.site.id]
    );

    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Locations Available
          </h3>
          <div className="text-gray-600 space-y-2">
            {sitesWithoutCoordinates.length > 0 ? (
              <>
                <p>
                  Found {sitesWithoutCoordinates.length} sites with addresses
                  that couldn't be geocoded.
                </p>
                <p className="text-sm">
                  Please check your Google Maps API key configuration and ensure
                  the Geocoding API is enabled.
                </p>
              </>
            ) : (
              <p>
                No sites with location coordinates and devices found. Sites need
                to have either address coordinates or a valid address to appear
                on the map.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Map Statistics Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">
                {getHeaderTitle()}
              </span>
              <span className="text-xs text-gray-500">
                ({mapLocations.length} sites on map)
              </span>
              {geocodingLoading && (
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              )}
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <Activity className="w-4 h-4 text-orange-500" />
                <span>
                  {sitesSummary.reduce((sum, s) => sum + s.iqwatchCount, 0)}{" "}
                  iQWatch
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-600">
                {sitesSummary.reduce((sum, s) => sum + s.onlineCount, 0)} Online
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-gray-600">
                {sitesSummary.reduce((sum, s) => sum + s.offlineCount, 0)}{" "}
                Offline
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 min-h-0">
        <GoogleMap
          locations={mapLocations}
          center={mapCenter}
          zoom={mapZoom}
          height="100%"
          className="w-full h-full"
          onLocationClick={handleLocationClick}
          apiKey={apiKey}
          showControls={true}
        />
      </div>

      {/* Slide-in Panel */}
      {selectedSiteForPanel && selectedSiteOrgId && (
        <SiteDetailSlidePanel
          site={selectedSiteForPanel}
          iqwatchDevices={iqwatchDevices}
          organizationId={selectedSiteOrgId}
          isOpen={!!selectedSiteForPanel}
          onClose={() => setSelectedSiteForPanel(null)}
          onDeviceClick={onDeviceClick}
        />
      )}
    </div>
  );
};

export default DashboardMapView;
